import { LitElement, html, css, utils } from '@hh/global.js'
const { stringToHTML } = utils

class HHSizeGuide extends LitElement {
  static properties = {
    _table: {
      state: true,
    },
    _tableTitle: {
      state: true,
    },
  }

  constructor() {
    super()

    this._table = null
    this._tableTitle = null
  }

  connectedCallback() {
    super.connectedCallback()

    this.observer = new MutationObserver((mutationList, observer) => {
      for (const { addedNodes } of mutationList) {
        const sizeGuideContainer = [...addedNodes].find((node) =>
          node.classList.contains('esc-size-guide')
        )

        this._tableTitle = sizeGuideContainer?.querySelector(
          '.esc-size-guide--title'
        ).innerHTML

        this._table = sizeGuideContainer?.querySelector('table')
      }
    }).observe(this, { childList: true, subtree: true })
  }

  updated() {
    if (this._table) {
      this.dispatchEvent(
        new CustomEvent('sizeGuideLoaded', {
          bubbles: true,
          composed: true,
          detail: {
            loaded: true,
          },
        })
      )
      this.observer?.disconnect()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.observer.disconnect()
  }

  getTableTitle() {
    return this._tableTitle
  }

  getTableMarkup() {
    return stringToHTML(this._table.outerHTML)
  }

  render() {
    if (!this._table) return html`<slot style="display: none;></slot>`

    return html`
      <hh-modal
        id="size-guide"
        overlay
        scroll-lock
        ignore-touch-scroll-lock
        size="flyout"
      >
        <span class="h6 size-guide__close" slot="close">Close</span>

        <div class="size-guide">
          <slot name="title"></slot>
          <slot name="image"></slot>

          <div class="size-guide__inner">
            <h6 class="size-guide__title">${this.getTableTitle()}</h6>
            <div class="size-guide__table">${this.getTableMarkup()}</div>
          </div>
        </div>
      </hh-modal>
    `
  }

  static styles = css`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    hh-modal {
      --hh-padding: 3rem 1rem 1rem;
      --hh-background: var(--hh-color-day);
      --hh-border-color: var(--hh-color-neutral);
      --hh-max-width: 100%;
      --hh-flyout-offset: 0;
      --hh-flyout-height: 100%;
      --hh-close-top: 3rem;
      --hh-close-right: 1rem;
    }

    h6,
    .h6,
    .size-guide__table tr:first-child td:not(:first-child) {
      font: var(--hh-h6-font);
      text-transform: var(--hh-h6-text-transform);
      letter-spacing: var(--hh-h6-letter-spacing);
    }

    .size-guide__table {
      width: 100vw;
      margin-left: -1rem;
      padding: 0 1rem;
      overflow-x: auto;
      touch-action: pan-x;
      overscroll-behavior: contain;
    }

    .size-guide__table table {
      width: 100%;
      border-collapse: collapse;
    }

    .size-guide__table tr:first-child td:first-child {
      color: var(--hh-color-subdued, #666);
    }

    .size-guide__table td {
      border: 0.1rem solid var(--hh-color-night, #000);
      padding: 1rem;
    }

    .size-guide__table td:not(:first-child) {
      min-width: 6rem;
      text-align: center;
    }

    .size-guide__title {
      margin-top: 0;
      margin-bottom: 1.6rem;
    }

    .size-guide__close {
      background-color: var(--hh-color-day);
      padding: 1rem;
    }

    ::slotted([slot='title']) {
      width: 100%;
      margin: 0 0 2.4rem;
    }

    ::slotted([slot='image']) {
      margin: 2.4rem 0 3.2rem;
    }

    @media screen and (min-width: 750px) {
      hh-modal {
        --hh-max-width: 96rem;
        --hh-flyout-height: auto;
        --hh-padding: 2rem;
        --hh-flyout-offset-top: 12rem;
        --hh-flyout-offset-right: 50%;
        --hh-flyout-transform: translateX(50%);
        --hh-close-top: 2rem;
        --hh-close-right: 2rem;
      }

      .size-guide {
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;
      }

      .size-guide__inner {
        flex: 0 0 68%;
        max-width: 68%;
      }

      .size-guide__table {
        width: 100%;
        margin-left: 0;
        padding: 0;
      }

      ::slotted([slot='image']) {
        margin: 0;
        padding-right: 2.4rem;
        flex: 0 0 32%;
        max-width: 32%;
      }
    }
  `
}

customElements.define('hh-size-guide', HHSizeGuide)
